import { APP_INITIALIZER, ErrorHandler, isDevMode, NgModule, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { from, Observable } from 'rxjs';
import {
  BrowserModule,
  provideClientHydration,
} from '@angular/platform-browser';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { CookieService } from 'ngx-cookie-service';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { CartEffects } from '../../../shared/effects/cart.effect';
import { UserEffects } from '../../../shared/effects/user.effect';
import { CheckoutEffects } from '../../../shared/effects/checkout.effect';
import { WalletEffects } from 'projects/shared/effects/wallet.effect';
import { ProductEffects } from 'projects/shared/effects/product.effect';
import { NotificationEffects } from 'projects/shared/effects/notification.effect';
import { TicketSystemEffects } from 'projects/shared/effects/ticket-system.effect';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ConfirmationService, MessageService } from 'primeng/api';
import {
  HttpClient,
} from '@angular/common/http';
import { DevAccessInterceptor } from 'projects/shared/interceptor/access-key.interceptor';
import { LayoutsModule } from './layout/layouts.module';
import {
  TranslateLoader,
  TranslateModule,
  TranslateService,
} from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { MobileToaster } from './../../../shared/components/mobile-toaster/mobile-toaster.component';
import { SentryInterceptor } from './../../../shared/interceptor/sentry.interceptor';
import * as Sentry from "@sentry/angular";
import { SessionExpiredInterceptor } from './../../../shared/interceptor/session-expired.interceptor';
import { NavigateToDirective } from 'projects/shared/directives/navigator.directive';
import { OtpInputComponent } from "projects/shared/components/OTP/otp-input.component";
import { CredentialsInterceptor } from 'projects/shared/interceptor/credentials.interceptor';
import { NetworkRetryInterceptor } from 'projects/shared/interceptor/network-retry.interceptor';
import { MaintenanceInterceptor } from 'projects/shared/interceptor/maintenance.interceptor';
import { transferStateInterceptor } from 'projects/shared/interceptors/transfer-state.interceptor';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { RegionalReducer } from './layout/navbar/components/navbar-header/partials/nav-regional/store/regional-setting.reducer';
import { RegionalSettingEffects } from './layout/navbar/components/navbar-header/partials/nav-regional/store/regional-setting.effects';
import { SuggestionReducer } from './modules/home/components/suggest-populerin-tour-country/store/suggestions.reducer';
import { CartReducer } from './layout/navbar/components/navbar-header/partials/nav-cart/store/cart.reducer';
import { CartEffect } from './layout/navbar/components/navbar-header/partials/nav-cart/store/cart.effect';
import { UserEffect } from './modules/user-settings/components/profile/store/profile.effects';
import { UserReducer } from './modules/user-settings/components/profile/store/profile.reducer';
import { UserCountryInterceptor } from 'projects/shared/interceptor/user-country.interceptor';
import { MaintenanceComponent } from './components/maintenance/maintenance.component';
import { AppInitializerService, appInitializerFactory } from './+shared/services/app-initializer.service';
import { metaReducers } from 'projects/shared/reducers/storage.meta-reducer';
import { ThemeReducer } from './layout/navbar/components/navbar-header/store/navbar.reducer';
import { ToastContainerComponent } from 'projects/shared/components/toaster/toaster.component';


@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserAnimationsModule,
    AppRoutingModule,
    LayoutsModule,
    MobileToaster,
    NavigateToDirective,
    MaintenanceComponent,
    EffectsModule.forFeature([
      // UserEffects,
      // CategoryEffects,
      WalletEffects,
      ProductEffects,
      NotificationEffects,
      CartEffects,
      TicketSystemEffects,
      CheckoutEffects,
      RegionalSettingEffects,
      CartEffect,
      UserEffect,
      // DialogModule
    ]),
    ToastContainerComponent,
    EffectsModule.forRoot(),
    StoreModule.forRoot({}, { metaReducers }),
    StoreModule.forFeature('regionalSetting', RegionalReducer),
    StoreModule.forFeature('suggestions', SuggestionReducer),
    StoreModule.forFeature('cart', CartReducer),
    StoreModule.forFeature('user', UserReducer),
    StoreModule.forFeature('navbar', ThemeReducer),
    StoreDevtoolsModule.instrument({
      maxAge: 25,
      logOnly: !isDevMode(),
      autoPause: true,
      trace: false,
      traceLimit: 75,
      connectInZone: true
    }),

    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient, PLATFORM_ID],
      },
    }),
    BrowserModule.withServerTransition({ appId: 'serverApp' }),
    OtpInputComponent
  ],
  providers: [
    CookieService,
    MessageService,
    ConfirmationService,
    // ✅ App Initializer - loads critical data before app renders (SSR + Browser)
    {
      provide: APP_INITIALIZER,
      useFactory: appInitializerFactory,
      deps: [AppInitializerService],
      multi: true,
    },
    // ✅ Translate Initializer - sets default language
    {
      provide: APP_INITIALIZER,
      useFactory: translateInitializerFactory,
      deps: [TranslateService],
      multi: true,
    },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
    // ✅ Client Hydration - preserves SSR HTML instead of destroying it
    provideClientHydration(),
    provideHttpClient(
      withInterceptors([
        transferStateInterceptor, // Must be first to cache requests
        NetworkRetryInterceptor,
        DevAccessInterceptor,
        CredentialsInterceptor,
        SessionExpiredInterceptor,
        SentryInterceptor,
        UserCountryInterceptor,
        MaintenanceInterceptor,
      ]),
      withFetch(),
    ),

  ],
  bootstrap: [AppComponent],
})
export class AppModule { }

/**
 * The i18n JSON files keep the same URL across deploys but are served with a 1-year max-age,
 * so browsers kept old copies and new keys showed up raw (e.g. "subCategory.payArea.loginBox.hint").
 * In the browser, load them with cache mode "no-cache": the cached copy is always revalidated
 * (a cheap 304 when unchanged), so a deploy's new translations show up right away.
 */
export class RevalidatingTranslateLoader implements TranslateLoader {
  constructor(private prefix = '/assets/i18n/', private suffix = '.json') { }

  getTranslation(lang: string): Observable<any> {
    return from(
      fetch(`${this.prefix}${lang}${this.suffix}`, { cache: 'no-cache' }).then(res => {
        if (!res.ok) throw new Error(`Failed to load translations "${lang}": ${res.status}`);
        return res.json();
      })
    );
  }
}

export function HttpLoaderFactory(http: HttpClient, platformId: object) {
  // Server-side rendering keeps the existing HttpClient loader
  return isPlatformBrowser(platformId)
    ? new RevalidatingTranslateLoader()
    : new TranslateHttpLoader(http, '/assets/i18n/', '.json');
}

/**
 * Translate initializer - sets default language
 */
export function translateInitializerFactory(
  translate: TranslateService,
): () => Promise<void> {
  return () =>
    new Promise((resolve) => {
      translate.setDefaultLang('en');
      translate.use('en').subscribe(() => {
        resolve();
      });
    });
}